import React, { useState, useEffect } from 'react';
import { database } from '../../firebase';
import { ref, push, onValue, query, orderByChild, limitToLast, get } from 'firebase/database';
import './CountryQuizGame.css';

const CountryQuizGame = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [timeLeft, setTimeLeft] = useState(30);
  const [playerName, setPlayerName] = useState('');
  const [gameStarted, setGameStarted] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [currentRank, setCurrentRank] = useState(null);
  const [questionType, setQuestionType] = useState('');
  const [wrongAnswers, setWrongAnswers] = useState(0);
  const [allQuestions, setAllQuestions] = useState([]);
  const [usedQuestionIds, setUsedQuestionIds] = useState(new Set());
  const [isLoadingQuestion, setIsLoadingQuestion] = useState(false);
  const [timeUpProcessed, setTimeUpProcessed] = useState(false);

  const [currentQuestionData, setCurrentQuestionData] = useState(null);

  // Load all questions from Firebase on component mount
  useEffect(() => {
    loadQuestionsFromFirebase();
  }, []);

  const loadQuestionsFromFirebase = async () => {
    try {
      const questionsRef = ref(database, 'quiz-questions');
      const snapshot = await get(questionsRef);
      
      if (snapshot.exists()) {
        const questionsData = snapshot.val();
        const questionsArray = Object.keys(questionsData).map(key => ({
          id: key,
          ...questionsData[key]
        }));
        setAllQuestions(questionsArray);
      } else {
        // If no questions exist, create some initial questions
        await createInitialQuestions();
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      // Fallback to local questions if Firebase fails
      setAllQuestions(getLocalQuestions());
    }
  };

  const createInitialQuestions = async () => {
    const initialQuestions = getLocalQuestions();
    const questionsRef = ref(database, 'quiz-questions');
    
    for (const question of initialQuestions) {
      await push(questionsRef, question);
    }
    
    // Reload questions after creating them
    loadQuestionsFromFirebase();
  };

  const getLocalQuestions = () => [
    // Country to Capital
    { country: "France", capital: "Paris", currency: "Euro", type: "capital", question: "What is the capital of France?", options: ["Paris", "London", "Berlin", "Madrid"], correct: "Paris" },
    { country: "Japan", capital: "Tokyo", currency: "Yen", type: "capital", question: "What is the capital of Japan?", options: ["Osaka", "Tokyo", "Kyoto", "Hiroshima"], correct: "Tokyo" },
    { country: "Brazil", capital: "Brasília", currency: "Real", type: "capital", question: "What is the capital of Brazil?", options: ["Rio de Janeiro", "São Paulo", "Brasília", "Salvador"], correct: "Brasília" },
    { country: "Australia", capital: "Canberra", currency: "Australian Dollar", type: "capital", question: "What is the capital of Australia?", options: ["Sydney", "Melbourne", "Brisbane", "Canberra"], correct: "Canberra" },
    { country: "Canada", capital: "Ottawa", currency: "Canadian Dollar", type: "capital", question: "What is the capital of Canada?", options: ["Toronto", "Vancouver", "Montreal", "Ottawa"], correct: "Ottawa" },
    
    // Country to Currency
    { country: "United Kingdom", capital: "London", currency: "Pound Sterling", type: "currency", question: "What is the currency of United Kingdom?", options: ["Euro", "Dollar", "Pound Sterling", "Franc"], correct: "Pound Sterling" },
    { country: "India", capital: "New Delhi", currency: "Rupee", type: "currency", question: "What is the currency of India?", options: ["Rupee", "Yuan", "Won", "Baht"], correct: "Rupee" },
    { country: "China", capital: "Beijing", currency: "Yuan", type: "currency", question: "What is the currency of China?", options: ["Yen", "Yuan", "Won", "Rupee"], correct: "Yuan" },
    { country: "Russia", capital: "Moscow", currency: "Ruble", type: "currency", question: "What is the currency of Russia?", options: ["Ruble", "Krona", "Zloty", "Lira"], correct: "Ruble" },
    { country: "Switzerland", capital: "Bern", currency: "Swiss Franc", type: "currency", question: "What is the currency of Switzerland?", options: ["Euro", "Swiss Franc", "Pound", "Krona"], correct: "Swiss Franc" },
    
    // Capital to Country
    { country: "Germany", capital: "Berlin", currency: "Euro", type: "country", question: "Berlin is the capital of which country?", options: ["Austria", "Germany", "Poland", "Netherlands"], correct: "Germany" },
    { country: "Italy", capital: "Rome", currency: "Euro", type: "country", question: "Rome is the capital of which country?", options: ["Greece", "Spain", "Italy", "Portugal"], correct: "Italy" },
    { country: "Egypt", capital: "Cairo", currency: "Egyptian Pound", type: "country", question: "Cairo is the capital of which country?", options: ["Libya", "Sudan", "Egypt", "Morocco"], correct: "Egypt" },
    { country: "Thailand", capital: "Bangkok", currency: "Baht", type: "country", question: "Bangkok is the capital of which country?", options: ["Vietnam", "Thailand", "Cambodia", "Laos"], correct: "Thailand" },
    { country: "Argentina", capital: "Buenos Aires", currency: "Peso", type: "country", question: "Buenos Aires is the capital of which country?", options: ["Chile", "Uruguay", "Argentina", "Paraguay"], correct: "Argentina" },
    
    // Mixed advanced questions
    { country: "Norway", capital: "Oslo", currency: "Norwegian Krone", type: "capital", question: "What is the capital of Norway?", options: ["Stockholm", "Helsinki", "Copenhagen", "Oslo"], correct: "Oslo" },
    { country: "South Korea", capital: "Seoul", currency: "Won", type: "currency", question: "What is the currency of South Korea?", options: ["Yen", "Yuan", "Won", "Ringgit"], correct: "Won" },
    { country: "Netherlands", capital: "Amsterdam", currency: "Euro", type: "country", question: "Amsterdam is the capital of which country?", options: ["Belgium", "Netherlands", "Luxembourg", "Denmark"], correct: "Netherlands" },
    { country: "Mexico", capital: "Mexico City", currency: "Peso", type: "capital", question: "What is the capital of Mexico?", options: ["Guadalajara", "Monterrey", "Mexico City", "Tijuana"], correct: "Mexico City" },
    { country: "Turkey", capital: "Ankara", currency: "Turkish Lira", type: "currency", question: "What is the currency of Turkey?", options: ["Turkish Lira", "Dinar", "Dirham", "Rial"], correct: "Turkish Lira" },
    
    // Additional challenging questions
    { country: "New Zealand", capital: "Wellington", currency: "New Zealand Dollar", type: "capital", question: "What is the capital of New Zealand?", options: ["Auckland", "Wellington", "Christchurch", "Hamilton"], correct: "Wellington" },
    { country: "South Africa", capital: "Cape Town", currency: "Rand", type: "currency", question: "What is the currency of South Africa?", options: ["Rand", "Shilling", "Kwacha", "Birr"], correct: "Rand" },
    { country: "Sweden", capital: "Stockholm", currency: "Swedish Krona", type: "country", question: "Stockholm is the capital of which country?", options: ["Norway", "Denmark", "Finland", "Sweden"], correct: "Sweden" },
    { country: "Vietnam", capital: "Hanoi", currency: "Dong", type: "capital", question: "What is the capital of Vietnam?", options: ["Ho Chi Minh City", "Hanoi", "Da Nang", "Hue"], correct: "Hanoi" },
    { country: "Chile", capital: "Santiago", currency: "Chilean Peso", type: "currency", question: "What is the currency of Chile?", options: ["Bolivar", "Sol", "Chilean Peso", "Guarani"], correct: "Chilean Peso" }
  ];

  const getRandomQuestion = () => {
    if (allQuestions.length === 0) return null;
    
    // Filter out used questions
    const availableQuestions = allQuestions.filter(q => !usedQuestionIds.has(q.id));
    
    // If all questions are used, reset the used questions set
    if (availableQuestions.length === 0) {
      setUsedQuestionIds(new Set());
      return allQuestions[Math.floor(Math.random() * allQuestions.length)];
    }
    
    // Return a random unused question
    const randomQuestion = availableQuestions[Math.floor(Math.random() * availableQuestions.length)];
    setUsedQuestionIds(prev => new Set([...prev, randomQuestion.id]));
    
    return randomQuestion;
  };

  useEffect(() => {
    let timer;
    if (gameStarted && timeLeft > 0 && !showResult && !gameOver && !timeUpProcessed && !isLoadingQuestion) {
      timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0 && !showResult && !gameOver && !timeUpProcessed && !isLoadingQuestion) {
      setTimeUpProcessed(true);
      handleTimeUp();
    }
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [timeLeft, gameStarted, showResult, gameOver, timeUpProcessed, isLoadingQuestion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = () => {
    const scoresRef = ref(database, 'quiz-scores');
    const topScoresQuery = query(scoresRef, orderByChild('score'), limitToLast(100));
    
    onValue(topScoresQuery, (snapshot) => {
      const scores = [];
      snapshot.forEach((childSnapshot) => {
        scores.push({
          id: childSnapshot.key,
          ...childSnapshot.val()
        });
      });
      
      // Sort by score (descending) and then by time (ascending for same scores)
      scores.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.timeSpent - b.timeSpent;
      });
      
      setLeaderboard(scores.slice(0, 10)); // Top 10
    });
  };

  const startGame = () => {
    if (playerName.trim() && allQuestions.length > 0) {
      setGameStarted(true);
      setCurrentQuestion(0);
      setScore(0);
      setCorrectAnswers(0);
      setTimeLeft(30);
      setShowResult(false);
      setGameOver(false);
      setWrongAnswers(0);
      setUsedQuestionIds(new Set());
      setTimeUpProcessed(false);
      
      // Load first question
      const firstQuestion = getRandomQuestion();
      setCurrentQuestionData(firstQuestion);
      if (firstQuestion) {
        setQuestionType(firstQuestion.type);
      }
    }
  };

  const handleTimeUp = () => {
    if (showResult || gameOver || timeUpProcessed) return; // Prevent multiple calls
    
    setShowResult(true);
    setSelectedAnswer(''); // No answer selected
    setTimeUpProcessed(true); // Mark as processed immediately
    
    // Time up counts as wrong answer
    const newWrongAnswers = wrongAnswers + 1;
    setWrongAnswers(newWrongAnswers);
    
    setTimeout(() => {
      if (newWrongAnswers >= 3) {
        endGame();
      } else {
        nextQuestion();
      }
    }, 2500);
  };

  const handleAnswerClick = (answer) => {
    if (showResult || timeUpProcessed) return; // Prevent multiple clicks and conflicts with timeout
    
    setSelectedAnswer(answer);
    setShowResult(true);
    setTimeUpProcessed(true); // Prevent timer from also triggering
    
    const isCorrect = answer === currentQuestionData.correct;
    
    if (isCorrect) {
      const timeBonus = Math.floor(timeLeft / 3);
      const newScore = score + 10 + timeBonus;
      setScore(newScore);
      setCorrectAnswers(correctAnswers + 1);
      
      setTimeout(() => {
        nextQuestion();
      }, 2500);
    } else {
      const newWrongAnswers = wrongAnswers + 1;
      setWrongAnswers(newWrongAnswers);
      
      setTimeout(() => {
        if (newWrongAnswers >= 3) {
          endGame();
        } else {
          nextQuestion();
        }
      }, 2500);
    }
  };

  const nextQuestion = () => {
    if (gameOver || isLoadingQuestion) return; // Prevent calling when game is over or already loading
    
    setIsLoadingQuestion(true);
    
    // Reset all states immediately
    setShowResult(false);
    setTimeUpProcessed(false);
    setSelectedAnswer('');
    
    // Move to next question number
    const nextQ = currentQuestion + 1;
    setCurrentQuestion(nextQ);
    
    // Get next random question
    const nextQuestionData = getRandomQuestion();
    
    if (nextQuestionData && allQuestions.length > 0) {
      setCurrentQuestionData(nextQuestionData);
      setQuestionType(nextQuestionData.type);
      setTimeLeft(30);
      setIsLoadingQuestion(false);
    } else {
      // Fallback: try to get any question from allQuestions
      if (allQuestions.length > 0) {
        const fallbackQuestion = allQuestions[Math.floor(Math.random() * allQuestions.length)];
        setCurrentQuestionData(fallbackQuestion);
        setQuestionType(fallbackQuestion.type);
        setTimeLeft(30);
        setIsLoadingQuestion(false);
        // Reset used questions set since we're falling back
        setUsedQuestionIds(new Set([fallbackQuestion.id]));
      } else {
        // No questions available at all - end game
        endGame();
      }
    }
  };

  const endGame = () => {
    setGameOver(true);
    setGameStarted(false);
    setIsLoadingQuestion(false); // Ensure loading state is cleared
    saveScore();
  };

  const saveScore = () => {
    const gameData = {
      playerName: playerName.trim(),
      score: score,
      correctAnswers: correctAnswers,
      questionsAnswered: currentQuestion + 1,
      wrongAnswers: wrongAnswers,
      timeSpent: (currentQuestion + 1) * 30 - timeLeft,
      timestamp: Date.now(),
      date: new Date().toLocaleDateString()
    };

    const scoresRef = ref(database, 'quiz-scores');
    push(scoresRef, gameData).then(() => {
      fetchLeaderboard();
      calculateRank(gameData.score);
    });
  };

  const calculateRank = (playerScore) => {
    const allScores = [...leaderboard.map(entry => entry.score), playerScore];
    allScores.sort((a, b) => b - a);
    const rank = allScores.indexOf(playerScore) + 1;
    setCurrentRank(rank);
  };

  const resetGame = () => {
    setCurrentQuestion(0);
    setScore(0);
    setCorrectAnswers(0);
    setSelectedAnswer('');
    setShowResult(false);
    setGameOver(false);
    setTimeLeft(30);
    setGameStarted(false);
    setCurrentRank(null);
    setWrongAnswers(0);
    setUsedQuestionIds(new Set());
    setCurrentQuestionData(null);
    setTimeUpProcessed(false);
  };

  const getQuestionIcon = (type) => {
    switch(type) {
      case 'capital': return '🏛️';
      case 'currency': return '💰';
      case 'country': return '🌍';
      default: return '❓';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#4CAF50';
    if (score >= 60) return '#FF9800';
    return '#f44336';
  };

  if (!gameStarted && !gameOver) {
    return (
      <div className="quiz-container">
        <div className="quiz-header">
          <h1>🌍 Country Knowledge Quiz 🏛️💰</h1>
          <p>Test your knowledge of countries, capitals, and currencies!</p>
        </div>
        
        <div className="start-screen">
          <div className="quiz-game-info">
            <h2>How to Play:</h2>
            <ul>
              <li>🎯 Answer questions about countries, capitals, and currencies</li>
              <li>⏱️ Each question has a 30-second timer</li>
              <li>🏆 Earn points: 10 + time bonus for correct answers</li>
              <li>❌ Game ends after 3 wrong answers</li>
              <li>📊 Compete on the global leaderboard</li>
            </ul>
          </div>
          
          <div className="player-input">
            <label htmlFor="playerName">Enter Your Name:</label>
            <input
              type="text"
              id="playerName"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              placeholder="Your name here..."
              maxLength={20}
            />
            <button 
              onClick={startGame} 
              disabled={!playerName.trim()}
              className="start-button"
            >
              🚀 Start Quiz
            </button>
          </div>
          
          <div className="action-buttons">
            <button 
              onClick={() => setShowLeaderboard(!showLeaderboard)}
              className="leaderboard-button"
            >
              🏆 {showLeaderboard ? 'Hide' : 'Show'} Leaderboard
            </button>
          </div>
          
          {showLeaderboard && (
            <div className="leaderboard">
              <h3>🏆 Top 10 Players</h3>
              {leaderboard.length === 0 ? (
                <p>No scores yet. Be the first!</p>
              ) : (
                <div className="leaderboard-list">
                  <div className="leaderboard-header">
                    <span className="rank-header">Rank</span>
                    <span className="name-header">Player</span>
                    <span className="score-header">Score</span>
                  </div>
                  {leaderboard.map((entry, index) => (
                    <div key={entry.id} className="leaderboard-item">
                      <span className="rank">
                        {`#${index + 1}`}
                      </span>
                      <span className="name">{entry.playerName}</span>
                      <span className="score">{entry.score} pts</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="quiz-container">
        <div className="game-over">
          <div className="final-score" style={{ borderColor: getScoreColor(score) }}>
            <h2>{wrongAnswers >= 3 ? '💔 Game Over!' : '🎉 Game Complete!'}</h2>
            <div className="score-display">
              <div className="main-score">
                <span className="score-value">{score}</span>
                <span className="score-label">Final Score</span>
              </div>
              <div className="score-details">
                <div className="score-item">
                  <span className="icon">🎯</span>
                  <span className="value">{currentQuestion + 1}</span>
                  <span className="label">Questions</span>
                </div>
                <div className="score-item">
                  <span className="icon">✅</span>
                  <span className="value">{correctAnswers}</span>
                  <span className="label">Correct</span>
                </div>
                <div className="score-item">
                  <span className="icon">❌</span>
                  <span className="value">{wrongAnswers}</span>
                  <span className="label">Wrong</span>
                </div>
                {currentRank && (
                  <div className="score-item">
                    <span className="icon">🏆</span>
                    <span className="value">#{currentRank}</span>
                    <span className="label">Rank</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="action-buttons">
            <button onClick={resetGame} className="play-again-button">
              🔄 Play Again
            </button>
            <button 
              onClick={() => setShowLeaderboard(!showLeaderboard)}
              className="leaderboard-button"
            >
              🏆 {showLeaderboard ? 'Hide' : 'Show'} Leaderboard
            </button>
          </div>
          
          {showLeaderboard && (
            <div className="leaderboard">
              <h3>🏆 Top 10 Players</h3>
              <div className="leaderboard-list">
                <div className="leaderboard-header">
                  <span className="rank-header">Rank</span>
                  <span className="name-header">Player</span>
                  <span className="score-header">Score</span>
                </div>
                {leaderboard.map((entry, index) => (
                  <div key={entry.id} className="leaderboard-item">
                    <span className="rank">
                      {index === 0 && '🥇'}
                      {index === 1 && '🥈'}
                      {index === 2 && '🥉'}
                      {index > 2 && `#${index + 1}`}
                    </span>
                    <span className="name">{entry.playerName}</span>
                    <span className="score">{entry.score} pts</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (allQuestions.length === 0) {
    return <div className="loading">Loading questions from database...</div>;
  }

  if (!currentQuestionData && gameStarted && !gameOver) {
    return <div className="loading">Loading next question...</div>;
  }

  const isCorrect = selectedAnswer === currentQuestionData?.correct;
  const timeBonus = Math.floor(timeLeft / 3);

  return (
    <div className="quiz-container">
      <div className="quiz-header">
        <div className="score-info">
          <span className="score">Score: {score}</span>
          <span className="wrong-answers">❌ {wrongAnswers}/3</span>
        </div>
        <div className="progress-info">
          <span className="question-counter">
            Question #{currentQuestion + 1}
          </span>
          <div className="timer">
            <span className={`time ${timeLeft <= 10 ? 'warning' : ''}`}>
              ⏱️ {timeLeft}s
            </span>
          </div>
        </div>
      </div>
      
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${Math.max(0, 100 - (wrongAnswers / 3) * 100)}%` }}
        ></div>
      </div>
      
      <div className="question-container">
        <div className="question-type">
          <span className="type-icon">{getQuestionIcon(questionType)}</span>
          <span className="type-text">
            {questionType === 'capital' && 'Capital City'}
            {questionType === 'currency' && 'Currency'}
            {questionType === 'country' && 'Country'}
          </span>
        </div>
        
        <h2 className="question">{currentQuestionData?.question}</h2>
        
        <div className="options-grid">
          {currentQuestionData?.options.map((option, index) => (
            <button
              key={index}
              className={`option ${
                showResult 
                  ? option === currentQuestionData.correct 
                    ? 'correct' 
                    : option === selectedAnswer 
                      ? 'incorrect' 
                      : ''
                  : ''
              }`}
              onClick={() => handleAnswerClick(option)}
              disabled={showResult}
            >
              {option}
            </button>
          ))}
        </div>
        
        {showResult && (
          <div className={`result ${isCorrect ? 'correct' : 'incorrect'}`}>
            <div className="result-icon">
              {isCorrect ? '✅' : '❌'}
            </div>
            <div className="result-text">
              {isCorrect 
                ? `Correct! +${10 + timeBonus} points ${timeBonus > 0 ? `(+${timeBonus} time bonus)` : ''}` 
                : `Incorrect! The correct answer is: ${currentQuestionData.correct}`
              }
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CountryQuizGame;
